import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { addDays, format, parse, startOfWeek } from 'date-fns';
import {
  useSchedulingAvailability,
  useSchedulingSlots,
  type DateAvailability,
} from '@/hooks/useSchedulingAvailability';
import { DayBookingsTimeline } from './DayBookingsTimeline';
import { dateFnsLocale } from '@/utils/locale';

/**
 * Availability-aware time picker (#1142): a 7-day week strip with per-day
 * qualified free capacity, plus 15-minute slot suggestions for the selected
 * day. Overbooked slots stay selectable — picking one only warns ("the day's
 * work gets re-laid"), it never blocks.
 */

const AVAILABILITY_COLORS: Record<DateAvailability['availabilityLevel'], string> = {
  full: 'success.main',
  high: 'success.light',
  medium: 'warning.main',
  low: 'error.main',
  none: 'text.disabled',
};

export interface AvailabilityTimePickerProps {
  /** Wizard state: "YYYY-MM-DDTHH:mm" local wallclock, or ''. */
  value: string;
  onChange: (value: string) => void;
  /** 'exact' shows the slot grid; 'floating' picks only the day (#1138). */
  mode: 'exact' | 'floating';
  estimatedDurationHours?: number;
  serviceIds: string[];
  mechanicIds?: string[];
}

function toDayStr(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** Free hours as the day card prints them. */
function freeHoursShown(freeMinutes: number | null | undefined): number {
  return Math.round((freeMinutes ?? 0) / 60);
}

/** Hours per mechanic as the breakdown prints them. */
function capacityHoursPerMechanic(info: { totalCapacityMinutes: number; availableMechanicsCount: number }): number {
  return info.availableMechanicsCount > 0 ? Math.round(info.totalCapacityMinutes / info.availableMechanicsCount / 60) : 0;
}

export function AvailabilityTimePicker({
  value,
  onChange,
  mode,
  estimatedDurationHours,
  serviceIds,
  mechanicIds,
}: AvailabilityTimePickerProps) {
  const { t } = useTranslation('workorders');
  const locale = dateFnsLocale();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayStr = toDayStr(today);

  const selectedDay = value ? value.split('T')[0] ?? '' : '';
  const selectedTime = value.split('T')[1] ?? '';

  // Week strip anchored to the selected day's week (or the current week).
  const [weekStart, setWeekStart] = useState<Date>(() =>
    startOfWeek(selectedDay ? parse(selectedDay, 'yyyy-MM-dd', new Date()) : today, { weekStartsOn: 1 }),
  );
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const isCurrentWeek = weekStart <= today;

  const { data: weekAvailability, isLoading: weekLoading } = useSchedulingAvailability({
    startDate: toDayStr(weekStart),
    endDate: toDayStr(addDays(weekStart, 6)),
    ...(estimatedDurationHours !== undefined ? { estimatedDurationHours } : {}),
    ...(mechanicIds && mechanicIds.length > 0 ? { mechanicIds } : {}),
    ...(serviceIds.length > 0 ? { serviceIds } : {}),
  });
  const dayInfo = useMemo(() => {
    const map = new Map<string, DateAvailability>();
    for (const d of weekAvailability ?? []) map.set(d.date, d);
    return map;
  }, [weekAvailability]);

  const { data: daySlots, isLoading: slotsLoading } = useSchedulingSlots(
    mode === 'exact' && selectedDay
      ? {
          date: selectedDay,
          ...(estimatedDurationHours !== undefined ? { estimatedDurationHours } : {}),
          ...(serviceIds.length > 0 ? { serviceIds } : {}),
        }
      : null,
  );

  const handleDayClick = (dayStr: string) => {
    if (mode === 'floating') {
      onChange(`${dayStr}T00:00`);
      return;
    }
    // Exact: default to the day's opening time so the wizard can proceed even
    // before a slot is refined; the slot grid below narrows it down.
    const open = dayInfo.get(dayStr)?.openTime ?? '08:00';
    onChange(`${dayStr}T${open}`);
  };

  const selectedSlot = daySlots?.slots.find((s) => s.start === selectedTime);
  const selectedDayInfo = selectedDay ? dayInfo.get(selectedDay) : undefined;
  const timelineOpen = daySlots?.openTime ?? selectedDayInfo?.openTime;
  const timelineClose = daySlots?.closeTime;

  /**
   * Why the day has no free capacity — the three cases used to collapse into
   * one message, "the time is already booked", which was a lie on an empty day
   * whose services nobody is qualified for (#1932):
   *
   * - `noMechanicsAtWork` — nobody is at work at all (holiday, sick leave).
   * - `noQualifiedMechanics` — people are at work, but none holds the selected
   *   services' required skills (#1140). Nothing is booked; the day is simply
   *   not offerable to THIS work, and it stays selectable.
   * - `dayFull` — qualified mechanics exist and every offerable slot is taken.
   *
   * Only when qualified capacity exists can a single slot be "already booked".
   */
  const qualifiedMechanics = daySlots?.totalMechanics ?? 0;
  const mechanicsAtWork = daySlots?.mechanicsAtWork;
  const offerableSlots = daySlots?.slots.filter((s) => !s.isPast) ?? [];
  const showSlotCapacityNotice = mode === 'exact' && !!daySlots && !daySlots.isClosed;

  const noMechanicsAtWork = showSlotCapacityNotice && mechanicsAtWork === 0;
  const noQualifiedMechanics =
    showSlotCapacityNotice && qualifiedMechanics === 0 && mechanicsAtWork !== 0;
  const dayFull =
    showSlotCapacityNotice &&
    qualifiedMechanics > 0 &&
    offerableSlots.length > 0 &&
    offerableSlots.every((s) => s.availableMechanics === 0);
  const showOverbookedWarning =
    showSlotCapacityNotice &&
    qualifiedMechanics > 0 &&
    !dayFull &&
    !!selectedSlot &&
    selectedSlot.availableMechanics === 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Week strip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={t('create.previousWeek')}>
          <span>
            <IconButton
              size="small"
              onClick={() => { setWeekStart((w) => addDays(w, -7)); }}
              disabled={isCurrentWeek}
              aria-label={t('create.previousWeek')}
            >
              <ChevronLeftIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, flex: 1 }}>
          {weekDays.map((day) => {
            const dayStr = toDayStr(day);
            const info = dayInfo.get(dayStr);
            const isPastDay = dayStr < todayStr;
            const isClosed = info ? !info.isBusinessDay || info.isClosed : false;
            const disabled = isPastDay || isClosed;
            const isSelected = dayStr === selectedDay;
            const freeMinutes = info?.qualifiedCapacityMinutes !== undefined && info.qualifiedMechanicsCount !== undefined
              ? Math.max(0, info.qualifiedCapacityMinutes - info.usedCapacityMinutes)
              : info?.availableCapacityMinutes;

            return (
              <Card
                key={dayStr}
                variant="outlined"
                sx={{
                  borderColor: isSelected ? 'primary.main' : undefined,
                  borderWidth: isSelected ? 2 : 1,
                  opacity: disabled ? 0.45 : 1,
                }}
              >
                <CardActionArea
                  onClick={() => { handleDayClick(dayStr); }}
                  disabled={disabled}
                  sx={{ p: 0.75, textAlign: 'center' }}
                  aria-label={`${format(day, 'EEEE d.M.', { locale })}${isClosed ? ` — ${t('create.dayClosed')}` : ''}`}
                >
                  <Typography variant="caption" display="block" sx={{ textTransform: 'capitalize' }}>
                    {format(day, 'EEEEEE', { locale })}
                  </Typography>
                  <Typography variant="body2" fontWeight={isSelected ? 700 : 500}>
                    {format(day, 'd.M.')}
                  </Typography>
                  {weekLoading ? (
                    <Skeleton variant="text" width={32} sx={{ mx: 'auto' }} />
                  ) : isClosed ? (
                    <Typography variant="caption" color="text.disabled" display="block" noWrap>
                      {t('create.dayClosed')}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: AVAILABILITY_COLORS[info?.availabilityLevel ?? 'none'],
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        title={info ? t('create.capacityBreakdown', {
                          mechanics: info.qualifiedMechanicsCount ?? info.availableMechanicsCount,
                          hours: capacityHoursPerMechanic(info),
                          // Derived from the rounded hours shown on the card, so
                          // "mechanics × hours − used" always equals the free hours
                          // under it — also at a half hour, where rounding the free
                          // minutes and the used minutes separately went opposite ways.
                          used: Math.max(0, (info.qualifiedMechanicsCount ?? info.availableMechanicsCount) * capacityHoursPerMechanic(info) - freeHoursShown(freeMinutes)),
                        }) : undefined}
                      >
                        {t('create.freeHours', { hours: freeHoursShown(freeMinutes) })}
                      </Typography>
                    </Box>
                  )}
                </CardActionArea>
              </Card>
            );
          })}
        </Box>

        <Tooltip title={t('create.nextWeek')}>
          <IconButton
            size="small"
            onClick={() => { setWeekStart((w) => addDays(w, 7)); }}
            aria-label={t('create.nextWeek')}
          >
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Slot grid — exact mode only; floating needs just the day */}
      {mode === 'exact' && selectedDay && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <DayBookingsTimeline
            date={selectedDay}
            value={value}
            onChange={onChange}
            {...(timelineOpen !== undefined ? { openTime: timelineOpen } : {})}
            {...(timelineClose !== undefined ? { closeTime: timelineClose } : {})}
            {...(estimatedDurationHours !== undefined ? { estimatedDurationHours } : {})}
          />
          {slotsLoading ? (
            <Skeleton variant="rounded" height={72} />
          ) : daySlots?.isClosed ? (
            <Alert severity="info">
              {daySlots.closedReason
                ? t('create.dayClosedReason', { reason: daySlots.closedReason })
                : t('create.dayClosed')}
            </Alert>
          ) : daySlots && daySlots.slots.length > 0 ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('create.slotSuggestions', { date: format(parse(selectedDay, 'yyyy-MM-dd', new Date()), 'EEEE d.M.', { locale }) })}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {daySlots.slots.map((slot) => {
                  const isSelected = slot.start === selectedTime;
                  // With zero qualified capacity EVERY slot reads as
                  // overbooked, which painted the whole grid red on an empty
                  // day (#1932). The day-level notice below carries that case.
                  const overbooked = qualifiedMechanics > 0 && slot.availableMechanics === 0;
                  return (
                    <Tooltip
                      key={slot.start}
                      title={
                        slot.isPast
                          ? t('create.slotPast')
                          : t('create.slotCapacity', { free: slot.availableMechanics, total: daySlots.totalMechanics })
                      }
                    >
                      <span>
                        <Chip
                          label={slot.start}
                          size="small"
                          disabled={slot.isPast}
                          onClick={() => { onChange(`${selectedDay}T${slot.start}`); }}
                          color={isSelected ? 'primary' : overbooked ? 'error' : slot.fits ? 'default' : 'warning'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        />
                      </span>
                    </Tooltip>
                  );
                })}
              </Box>
              {noMechanicsAtWork && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('create.slotNoMechanicsAtWork')}
                </Alert>
              )}
              {noQualifiedMechanics && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('create.slotNoQualifiedMechanics')}
                </Alert>
              )}
              {dayFull && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('create.slotDayFullWarning')}
                </Alert>
              )}
              {showOverbookedWarning && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('create.slotOverbookedWarning')}
                </Alert>
              )}
            </>
          ) : null}
        </Box>
      )}
    </Box>
  );
}
